import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import {
  fetchMemberAccountLedger,
  fetchMemberAccountSummary,
} from '../../../controllers/memberAccountController';

import { formatMoneyWithCurrency } from '../../../utils/currencyDisplay';

const PAGE_SIZE = 20;

const formatMoney = formatMoneyWithCurrency;

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const SOURCE_TYPE_COLORS = {
  1: { bg: '#E7EDF4', color: '#0E4D94' },
  2: { bg: '#FFF3E0', color: '#E65100' },
  3: { bg: '#F3E5F5', color: '#7B1FA2' },
  4: { bg: '#E8F5E9', color: '#2E7D32' },
  5: { bg: '#E0F7FA', color: '#00695C' },
  6: { bg: '#FFF8E1', color: '#F57F17' },
};

const getSourceTypeColors = (sourceType) =>
  SOURCE_TYPE_COLORS[sourceType] || { bg: '#f5f5f5', color: '#666666' };

const getLedgerKindColors = (ledgerKind) => {
  if (Number(ledgerKind) === 1) {
    return { bg: '#DFFFEA', color: '#17C653', label: 'Ödeme' };
  }
  return { bg: '#FFEEF3', color: '#F8285A', label: 'Borç' };
};

const getStatusChipColor = (status) => {
  switch (status) {
    case 'Paid':
      return { bg: '#DFFFEA', color: '#17C653' };
    case 'Overdue':
    case 'Failed':
      return { bg: '#FFEEF3', color: '#F8285A' };
    case 'AwaitingApproval':
    case 'PartialPaid':
      return { bg: '#FFF8E1', color: '#F57F17' };
    case 'Cancelled':
    case 'Canceled':
      return { bg: '#f5f5f5', color: '#666666' };
    default:
      return { bg: '#E7EDF4', color: '#0E4D94' };
  }
};

const hasLegacySync = (legacy) =>
  legacy &&
  ((legacy.duesOpenCount || 0) > 0 ||
    (legacy.mealsOpenCount || 0) > 0 ||
    (legacy.duesRemainingAmount || 0) > 0 ||
    (legacy.mealsRemainingAmount || 0) > 0);

const MemberAccountSection = ({ memberId, active }) => {
  const navigate = useNavigate();

  const [includeSpouse, setIncludeSpouse] = useState(true);
  const [includeEventGuests, setIncludeEventGuests] = useState(true);
  const [includeMealGuests, setIncludeMealGuests] = useState(true);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');

  const [ledgerItems, setLedgerItems] = useState([]);
  const [ledgerMeta, setLedgerMeta] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState('');

  const [ledgerKind, setLedgerKind] = useState(0);
  const [sourceTypesFilter, setSourceTypesFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const scopeParams = {
    memberId,
    includeSpouse,
    includeEventGuests,
    includeMealGuests,
  };

  const loadSummary = useCallback(async () => {
    if (!memberId) return;
    setSummaryLoading(true);
    setSummaryError('');
    try {
      const data = await fetchMemberAccountSummary(scopeParams);
      setSummary(data);
    } catch (e) {
      setSummary(null);
      setSummaryError(e?.message || 'Hesap özeti alınamadı');
    } finally {
      setSummaryLoading(false);
    }
  }, [memberId, includeSpouse, includeEventGuests, includeMealGuests]);

  const loadLedger = useCallback(async (overridePage = page) => {
    if (!memberId) return;
    setLedgerLoading(true);
    setLedgerError('');
    try {
      const { data, meta } = await fetchMemberAccountLedger({
        ...scopeParams,
        ledgerKind,
        sourceTypes: sourceTypesFilter.length > 0 ? sourceTypesFilter : null,
        status: statusFilter || null,
        pageNumber: overridePage,
        pageSize: PAGE_SIZE,
      });
      setLedgerItems(Array.isArray(data) ? data : []);
      setLedgerMeta(meta || null);
    } catch (e) {
      setLedgerItems([]);
      setLedgerMeta(null);
      setLedgerError(e?.message || 'Hesap defteri alınamadı');
    } finally {
      setLedgerLoading(false);
    }
  }, [
    memberId,
    includeSpouse,
    includeEventGuests,
    includeMealGuests,
    ledgerKind,
    sourceTypesFilter,
    statusFilter,
    page,
  ]);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadSummary(), loadLedger(page)]);
  }, [loadSummary, loadLedger, page]);

  useEffect(() => {
    if (!active || !memberId) return;
    loadSummary();
  }, [active, memberId, loadSummary]);

  useEffect(() => {
    if (!active || !memberId) return;
    loadLedger(page);
  }, [active, memberId, loadLedger, page]);

  const handleCategoryClick = (sourceType) => {
    setSourceTypesFilter([sourceType]);
    setLedgerKind(1);
    setPage(1);
  };

  const clearFilters = () => {
    setSourceTypesFilter([]);
    setLedgerKind(0);
    setStatusFilter('');
    setPage(1);
  };

  const handleRowNavigate = (row) => {
    if (row.isLegacy) return;
    const sourceType = Number(row.sourceType);
    if (sourceType === 2) {
      const eventId = row.parentSourceId || row.sourceId;
      if (eventId) navigate(`/payments/event-dues/${eventId}`);
      return;
    }
    if (sourceType === 1 && row.sourceId) {
      navigate(`/payments/dues/${row.sourceId}`);
      return;
    }
    if (sourceType === 6) {
      const mealId = row.parentSourceId || row.sourceId;
      if (mealId) navigate(`/payments/weekly-meetings/${mealId}`);
      return;
    }
    if (sourceType === 3) {
      const purchaseId = row.parentSourceId || row.sourceId;
      if (purchaseId) navigate(`/payments/donation-cards?purchaseId=${purchaseId}`);
      return;
    }
    if (sourceType === 4) {
      const donationId = row.parentSourceId || row.sourceId;
      if (donationId) navigate(`/payments/project-payments?donationId=${donationId}`);
      return;
    }
    if (sourceType === 5) {
      const orderId = row.parentSourceId || row.sourceId;
      if (orderId) navigate(`/payments/misc?orderId=${orderId}`);
    }
  };

  const logoCariCode = summary?.logoCariCode;
  const loading = summaryLoading && !summary;
  const legacy = summary?.legacyLogoSync;

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AccountBalanceIcon sx={{ color: '#0E4D94', fontSize: 22 }} />
          <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#444444' }}>
            Hesap Özeti
          </Typography>
        </Stack>
        <Button
          variant="outlined"
          size="small"
          startIcon={summaryLoading || ledgerLoading ? <CircularProgress size={14} /> : <RefreshIcon sx={{ width: 16 }} />}
          onClick={refreshAll}
          disabled={summaryLoading || ledgerLoading}
          sx={{
            textTransform: 'none',
            fontSize: 13,
            borderRadius: '5px',
            borderColor: '#e6e6e6',
            color: '#444444',
            '&:hover': { borderColor: '#0E4D94', backgroundColor: '#E7EDF4' },
          }}
        >
          Yenile
        </Button>
      </Stack>

      {!logoCariCode && (
        <Alert severity="warning" sx={{ fontSize: '13px' }}>
          Logo cari kodu tanımlı değil. Aidat ve yemek borcu oluşturma işlemleri başarısız olabilir.
        </Alert>
      )}

      {summary?.subMember?.fullName && (
        <Chip
          size="small"
          label={`Alt üye: ${summary.subMember.fullName}`}
          sx={{ alignSelf: 'flex-start', fontSize: '12px', fontWeight: 500 }}
        />
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : summaryError ? (
        <Alert severity="error">{summaryError}</Alert>
      ) : summary ? (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Card variant="outlined" sx={{ flex: '1 1 180px', p: 2, borderColor: '#ececec', boxShadow: 'none' }}>
              <Typography sx={{ fontSize: '12px', color: '#a3a3a3', mb: 0.5 }}>Logo Cari Kod</Typography>
              <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#444444' }}>
                {logoCariCode || '-'}
              </Typography>
            </Card>
            <Card variant="outlined" sx={{ flex: '1 1 180px', p: 2, borderColor: '#FFEEF3', boxShadow: 'none', bgcolor: '#FFF9FA' }}>
              <Typography sx={{ fontSize: '12px', color: '#a3a3a3', mb: 0.5 }}>Açık Borç</Typography>
              <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#F8285A' }}>
                {formatMoney(summary.totalPendingAmount)}
              </Typography>
            </Card>
            <Card variant="outlined" sx={{ flex: '1 1 180px', p: 2, borderColor: '#DFFFEA', boxShadow: 'none', bgcolor: '#F9FFFB' }}>
              <Typography sx={{ fontSize: '12px', color: '#a3a3a3', mb: 0.5 }}>Ödenen Toplam</Typography>
              <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#17C653' }}>
                {formatMoney(summary.totalPaidAmount)}
              </Typography>
            </Card>
          </Box>

          {hasLegacySync(legacy) && (
            <Alert severity="info" sx={{ fontSize: '13px' }}>
              Eski Logo sync kayıtları: Aidat {legacy.duesOpenCount || 0} açık (
              {formatMoney(legacy.duesRemainingAmount)}), Yemek {legacy.mealsOpenCount || 0} açık (
              {formatMoney(legacy.mealsRemainingAmount)}). Bu tutarlar üst özete dahil değildir; tabloda
              &quot;Eski Logo kaydı&quot; olarak görünür.
            </Alert>
          )}

          {Array.isArray(summary.categories) && summary.categories.length > 0 && (
            <Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#666', mb: 1 }}>
                Kategoriler
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {summary.categories.map((cat) => {
                  const colors = getSourceTypeColors(cat.sourceType);
                  const isSelected = sourceTypesFilter.length === 1 && sourceTypesFilter[0] === cat.sourceType;
                  return (
                    <Chip
                      key={cat.sourceType}
                      clickable
                      onClick={() => handleCategoryClick(cat.sourceType)}
                      label={`${cat.sourceTypeName}: ${cat.pendingCount} açık / ${formatMoney(cat.pendingAmount)}`}
                      sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        backgroundColor: isSelected ? colors.color : colors.bg,
                        color: isSelected ? '#fff' : colors.color,
                        border: isSelected ? `1px solid ${colors.color}` : 'none',
                      }}
                    />
                  );
                })}
                {(sourceTypesFilter.length > 0 || ledgerKind !== 0 || statusFilter) && (
                  <Chip
                    label="Tümünü göster"
                    variant="outlined"
                    size="small"
                    onClick={clearFilters}
                    sx={{ fontSize: '12px' }}
                  />
                )}
              </Box>
            </Box>
          )}
        </>
      ) : null}

      <Box sx={{ pt: 1 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#666', mb: 1 }}>
          Kapsam
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeSpouse}
                onChange={(e) => {
                  setIncludeSpouse(e.target.checked);
                  setPage(1);
                }}
              />
            }
            label={<Typography sx={{ fontSize: '13px' }}>Eş dahil</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeEventGuests}
                onChange={(e) => {
                  setIncludeEventGuests(e.target.checked);
                  setPage(1);
                }}
              />
            }
            label={<Typography sx={{ fontSize: '13px' }}>Etkinlik misafirleri</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeMealGuests}
                onChange={(e) => {
                  setIncludeMealGuests(e.target.checked);
                  setPage(1);
                }}
              />
            }
            label={<Typography sx={{ fontSize: '13px' }}>Yemek misafirleri</Typography>}
          />
        </Stack>
      </Box>

      <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Tür</InputLabel>
          <Select
            label="Tür"
            value={ledgerKind}
            onChange={(e) => {
              setLedgerKind(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value={0}>Tümü</MenuItem>
            <MenuItem value={1}>Sadece borç</MenuItem>
            <MenuItem value={2}>Sadece ödeme</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Durum</InputLabel>
          <Select
            label="Durum"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">Tümü</MenuItem>
            <MenuItem value="Pending">Bekliyor</MenuItem>
            <MenuItem value="Paid">Ödendi</MenuItem>
            <MenuItem value="Overdue">Gecikmiş</MenuItem>
            <MenuItem value="AwaitingApproval">Onay Bekliyor</MenuItem>
            <MenuItem value="PartialPaid">Kısmen Ödendi</MenuItem>
            <MenuItem value="Cancelled">İptal</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Typography sx={{ fontSize: '15px', fontWeight: 500, color: '#444444' }}>
        Hareketler
      </Typography>

      {ledgerLoading && ledgerItems.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : ledgerError ? (
        <Alert severity="error">{ledgerError}</Alert>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ borderColor: '#ececec', boxShadow: 'none' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tür</TableCell>
                  <TableCell>Kategori</TableCell>
                  <TableCell>Başlık</TableCell>
                  <TableCell>Dönem</TableCell>
                  <TableCell>Kişi</TableCell>
                  <TableCell align="right">Tutar</TableCell>
                  <TableCell align="right">Kalan</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Tarih</TableCell>
                  <TableCell>Referans</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ledgerItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 3, color: '#a3a3a3' }}>
                      Hareket kaydı bulunamadı.
                    </TableCell>
                  </TableRow>
                ) : (
                  ledgerItems.map((row) => {
                    const kindColors = getLedgerKindColors(row.ledgerKind);
                    const catColors = getSourceTypeColors(row.sourceType);
                    const statusColors = getStatusChipColor(row.status);
                    const dateValue = Number(row.ledgerKind) === 1 ? row.paymentDate : row.dueDate;
                    const canNavigate = !row.isLegacy && [1, 2, 3, 4, 5, 6].includes(Number(row.sourceType));

                    return (
                      <TableRow
                        key={row.id}
                        hover={canNavigate}
                        onClick={() => canNavigate && handleRowNavigate(row)}
                        sx={{
                          cursor: canNavigate ? 'pointer' : 'default',
                          bgcolor: row.isLegacy ? '#fafafa' : 'inherit',
                          opacity: row.isLegacy ? 0.85 : 1,
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={row.ledgerKindName || kindColors.label}
                            size="small"
                            sx={{
                              fontSize: '11px',
                              height: 22,
                              backgroundColor: kindColors.bg,
                              color: kindColors.color,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.sourceTypeName || '-'}
                            size="small"
                            sx={{
                              fontSize: '11px',
                              height: 22,
                              backgroundColor: catColors.bg,
                              color: catColors.color,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography sx={{ fontSize: '13px' }} noWrap title={row.title || ''}>
                            {row.title || '-'}
                          </Typography>
                          {row.isLegacy && (
                            <Chip
                              label="Eski Logo kaydı"
                              size="small"
                              sx={{ mt: 0.5, fontSize: '10px', height: 20, bgcolor: '#eee', color: '#666' }}
                            />
                          )}
                        </TableCell>
                        <TableCell>{row.period || '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 140 }}>
                          <Typography sx={{ fontSize: '12px' }} noWrap>
                            {row.subjectTypeName && row.subjectName
                              ? `${row.subjectTypeName}: ${row.subjectName}`
                              : row.subjectName || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatMoney(row.amount, row.currency)}</TableCell>
                        <TableCell align="right">
                          {Number(row.remainingAmount) > 0
                            ? formatMoney(row.remainingAmount, row.currency)
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.statusName || row.status || '-'}
                            size="small"
                            sx={{
                              fontSize: '11px',
                              height: 22,
                              backgroundColor: statusColors.bg,
                              color: statusColors.color,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(dateValue)}</TableCell>
                        <TableCell sx={{ maxWidth: 120 }}>
                          <Typography sx={{ fontSize: '12px' }} noWrap title={row.externalReference || ''}>
                            {row.externalReference || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {ledgerMeta && ledgerMeta.totalPages > 1 && (
            <Stack direction="row" justifyContent="center" sx={{ pt: 1 }}>
              <Pagination
                count={ledgerMeta.totalPages}
                page={ledgerMeta.currentPage || page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size="small"
              />
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};

export default MemberAccountSection;
